import { useEffect, useState } from "react";
import { Upload } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { absUrl } from "../../lib/api";
import { useAuthed } from "../../lib/adminApi";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", price: "", category: "", badge: "", image_url: "", vegetarian: false, featured: false, special: false, sides: "", extra_ids: [] };

const toForm = (it) => ({
  name: it.name, description: it.description || "", price: String(it.price), category: it.category, badge: it.badge || "",
  image_url: it.image_url || "", vegetarian: !!it.vegetarian, featured: !!it.featured, special: !!it.special,
  sides: (it.sides || []).join(", "), extra_ids: it.extra_ids || [],
});

function Flag({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-3 py-2 text-sm">
      {label}<Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

export default function DishDialog({ open, onOpenChange, item, categories, extras, onSaved }) {
  const call = useAuthed();
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open) setForm(item ? toForm(item) : { ...EMPTY, category: categories[0] || "" });
  }, [open, item, categories]);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await call("post", "/admin/upload", fd);
      set({ image_url: r.data.url });
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.category) { toast.error("Name, price and category are required"); return; }
    const payload = {
      name: form.name, description: form.description, price: Number(form.price), category: form.category,
      badge: form.badge || null, image_url: form.image_url || null, vegetarian: form.vegetarian, featured: form.featured,
      special: form.special, sides: form.sides.split(",").map((s) => s.trim()).filter(Boolean), extra_ids: form.extra_ids,
    };
    try {
      if (item) await call("patch", `/admin/menu/${item.id}`, payload);
      else await call("post", "/admin/menu", payload);
      toast.success(item ? "Dish updated" : "Dish added");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save dish"); }
  };

  const toggleExtra = (id) => set({ extra_ids: form.extra_ids.includes(id) ? form.extra_ids.filter((x) => x !== id) : [...form.extra_ids, id] });
  const isSample = item?.image_is_sample && form.image_url === item.image_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Edit menu item" : "Add menu item"}</DialogTitle>
          <DialogDescription>Prices, photos and options update on the storefront straight away.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <label className="rounded-xl border-2 border-dashed border-gray-200 h-24 w-24 grid place-items-center cursor-pointer bg-gray-50 overflow-hidden shrink-0" data-testid="upload-photo">
              {form.image_url ? <img src={absUrl(form.image_url)} className="h-full w-full object-cover" alt="" /> : <Upload size={18} className="text-gray-400" />}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            </label>
            <p className="text-xs text-gray-500">
              {uploading ? "Uploading…" : isSample ? "Currently an illustrative sample image. Click to upload your real photo." : "Click to upload or change the photo (JPG / PNG / WEBP)."}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input data-testid="dish-name" value={form.name} onChange={(e) => set({ name: e.target.value })} /></div>
            <div><Label>Price (KSh)</Label><Input data-testid="dish-price" type="number" value={form.price} onChange={(e) => set({ price: e.target.value })} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea data-testid="dish-desc" value={form.description} onChange={(e) => set({ description: e.target.value })} /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set({ category: v })}>
                <SelectTrigger data-testid="dish-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Badge (optional)</Label><Input data-testid="dish-badge" value={form.badge} onChange={(e) => set({ badge: e.target.value })} placeholder="e.g. Popular" /></div>
            <div className="col-span-2">
              <Label>Free side choices (comma separated, first is default)</Label>
              <Input data-testid="dish-sides" value={form.sides} onChange={(e) => set({ sides: e.target.value })} placeholder="e.g. Ugali, Rice, Chapati" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Flag label="Vegetarian" checked={form.vegetarian} onChange={(v) => set({ vegetarian: v })} testid="dish-vegetarian" />
            <Flag label="Featured" checked={form.featured} onChange={(v) => set({ featured: v })} testid="dish-featured" />
            <Flag label="Special" checked={form.special} onChange={(v) => set({ special: v })} testid="dish-special" />
          </div>
          {extras.length > 0 && (
            <div>
              <Label>Paid extras customers can add</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {extras.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 text-sm rounded-lg border border-gray-100 px-2 py-1.5">
                    <Checkbox checked={form.extra_ids.includes(e.id)} onCheckedChange={() => toggleExtra(e.id)} data-testid={`dish-extra-${e.id}`} />
                    {e.name} <span className="ml-auto text-xs text-gray-500">KSh {e.price}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <Button onClick={save} className="w-full mlobox-gradient text-white rounded-full" data-testid="save-dish">Save dish</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
